/**
 * Shared API helper. Single source of truth for the backend base URL.
 *
 * NOTE: We use axios (XHR under the hood) instead of `fetch` because the
 * preview environment injects PostHog / Cloudflare Insights scripts that
 * monkey-patch `window.fetch` and consume the response body for telemetry
 * before our code can read it — resulting in "body stream already read"
 * errors and an empty response body on the client side. XHR is unaffected.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({
    baseURL: API,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
    // Treat all 2xx as success; everything else throws so callers can react.
    validateStatus: (s) => s >= 200 && s < 300,
});

function toAppError(axiosError) {
    const resp = axiosError.response;
    const status = resp?.status ?? 0;
    const payload = resp?.data ?? null;
    const detailRaw = payload && (payload.detail || payload.message);
    const detail =
        typeof detailRaw === "string"
            ? detailRaw
            : detailRaw
                ? JSON.stringify(detailRaw)
                : status
                    ? `HTTP ${status}`
                    : axiosError.message || "Network error";
    const err = new Error(detail);
    err.status = status;
    err.payload = payload;
    return err;
}

async function _request(method, path, body) {
    try {
        const r = await client.request({ method, url: path, data: body });
        return r.data;
    } catch (e) {
        throw toAppError(e);
    }
}

export const postJSON = (path, body) => _request("POST", path, body);
export const getJSON = (path) => _request("GET", path);
export const putJSON = (path, body) => _request("PUT", path, body);
export const patchJSON = (path, body) => _request("PATCH", path, body);
export const delJSON = (path) => _request("DELETE", path);
